// @ts-strict-ignore
import flatten from 'lodash/flatten';
import mapValues from 'lodash/mapValues';

import {IndustryKey, ResolvedIndustrySpec} from 'common/onboarding/immersive-onboarding/types';
import type {LocalizationStore} from 'common/i18n/store';
import {IndustrySpecs} from 'common/onboarding/immersive-onboarding/constants';

export function getResolvedIndustrySpec({
  industry,
  i18n,
}: {
  industry: IndustryKey;
  i18n: LocalizationStore;
}): ResolvedIndustrySpec {
  const {print} = i18n;

  const industrySpec = IndustrySpecs[industry];
  return {
    display_name: print(industrySpec.display_name),
    default_events: industrySpec.default_events.map((exampleEvent) => {
      return {
        name: print(exampleEvent.name),
        properties: exampleEvent.properties.map((property) => {
          return {
            name: print(property.name),
            breakdowns: property.breakdowns.map((breakdown) => print(breakdown)),
          };
        }),
      };
    }),
  };
}

export function getResolvedIndustrySpecs({i18n}: {i18n: LocalizationStore}): Record<IndustryKey, ResolvedIndustrySpec> {
  return mapValues(IndustrySpecs, (_, industry: IndustryKey) => {
    return getResolvedIndustrySpec({industry, i18n});
  });
}

export const getDefaultEventList = ({industry, i18n}: {industry: IndustryKey; i18n: LocalizationStore}): string[] => {
  return getResolvedIndustrySpec({industry, i18n}).default_events.map((event) => event.name);
};

export const getMapFromDictionary = (eventsPropertiesMap: {[key: string]: string[]}) => {
  return new Map(Object.keys(eventsPropertiesMap).map((event) => [event, eventsPropertiesMap[event] ?? []]));
};

export const getDefaultEventPropertiesMap = ({
  industry,
  events,
  i18n,
}: {
  industry: IndustryKey;
  events?: string[];
  i18n: LocalizationStore;
}): Map<string, string[]> => {
  const defaultEvents = getResolvedIndustrySpec({industry, i18n}).default_events;

  return new Map(
    (events ?? getDefaultEventList({industry, i18n})).map((event) => {
      const eventAsDefault = defaultEvents.filter((defaultEvent) => defaultEvent.name === event)?.[0];
      return [eventAsDefault?.name ?? event, eventAsDefault?.properties.map((prop) => prop.name) ?? []];
    }),
  );
};

export const getBreakdownValues = ({property, i18n}: {property: string; i18n: LocalizationStore}) => {
  const industrySpecs = getResolvedIndustrySpecs({i18n});
  const {t} = i18n;

  const properties = flatten(
    Object.keys(industrySpecs).flatMap((key) => {
      return industrySpecs[key].default_events.map((event) => event.properties);
    }),
  );

  return (
    properties.filter((defaultProperty) => defaultProperty.name === property)[0]?.breakdowns ?? [
      t(`onboarding.example.property-value.property-a`),
      t(`onboarding.example.property-value.property-b`),
      t(`onboarding.example.property-value.property-c`),
    ]
  );
};

export const inputValidator = (val) => {
  return val.trim().length > 0;
};
